import { useMemo, useEffect, useState } from 'react'
import { Box, Drawer, Menu, MenuItem, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4VisibleSeriesLeft,
  selectBeacon4VisibleSeriesRight,
  toggleBeacon4PanelSeries,
  resetBeacon4HiddenSeries,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4ActivitiesVisible,
  selectBeacon4SelectedActivities,
  selectBeacon4VisibleActivities,
  toggleBeacon4ActivityVisibility,
  resetBeacon4ActivityVisibility,
} from '../../reducers/Beacon4FiltersReducer'
import { selectBeacon4PerformanceNetworkOverviewDashboardByTech } from '../../reducers/Beacon4PerformanceNetworkOverviewKpiDataReducer'
import { assignActivityColors } from '../../utils/activityColors'
import Beacon4ActivityDetailsDialog from './Beacon4ActivityDetailsDialog'

// Distinct hues for Performance KPI series (chosen for clear differentiation)
const PALETTE = [
  '#1565c0', '#2e7d32', '#f57c00', '#6a1a9a', '#c62828',
  '#00897b', '#5d4037', '#ad1457', '#0277bd', '#7b1fa2',
]

function getColorForKey(key, idx) {
  return PALETTE[idx % PALETTE.length]
}

function LegendItem({ d, isHidden, onClick, shimmer }) {
  return (
    <Box onClick={() => onClick(d.key)} sx={{
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.3 : 1,
      transition: 'opacity 0.2s, background 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
      '&::after': shimmer ? {
        content: '""',
        position: 'absolute',
        top: 0, left: '-100%',
        width: '60%', height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
        transform: 'skewX(-20deg)',
        animation: `b4p-shimmer 2.2s linear infinite`,
        animationDelay: `${d._delay ?? 0}ms`,
      } : {},
      '@keyframes b4p-shimmer': {
        '0%':   { left: '-100%' },
        '100%': { left: '150%'  },
      },
    }}>
      <Box sx={{
        width: 24, flexShrink: 0,
        height: 3, borderRadius: 1, bgcolor: d.color,
      }} />
      <Typography sx={{
        fontSize: 12, fontWeight: isHidden ? 400 : 600,
        color: isHidden ? 'text.disabled' : d.color,
        lineHeight: 1.2,
      }}>
        {d.label}
      </Typography>
    </Box>
  )
}

// One row per selected activity — same swatch+label shape as LegendItem
// above, but the swatch is a small square (not a line segment) to visually
// distinguish "this is a marker colour" from "this is a series colour", and
// there's a secondary line for the Start→End date range underneath the
// title so the user can match a marker pair on the chart to its activity
// without hovering it. Clickable, same "click a label to filter" isolate
// interaction as the KPI series legend above — toggleBeacon4ActivityVisibility
// drives which activities the chart actually draws markers for.
// Right-click on the row opens a small context menu ("Details") instead of
// the browser's own — clicking Details opens Beacon4ActivityDetailsDialog
// with everything ActivityMarkerDto carries for this activity. Kept as
// per-row local state (contextMenu/detailsOpen) rather than lifted to the
// parent list: only one row's menu/dialog is ever open at a time, and this
// avoids threading activity-specific state through the parent for what's
// purely this row's own transient UI.
function ActivityLegendItem({ activity, isHidden, onClick }) {
  const title = activity.title || activity.feature || `Activity ${activity.id}`
  const [contextMenu, setContextMenu] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const handleContextMenu = (e) => {
    e.preventDefault()
    setContextMenu(contextMenu === null ? { mouseX: e.clientX, mouseY: e.clientY } : null)
  }
  const closeContextMenu = () => setContextMenu(null)
  const openDetails = () => { closeContextMenu(); setDetailsOpen(true) }

  return (
    <>
      <Box
        onClick={() => onClick(activity.id)}
        onContextMenu={handleContextMenu}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1, py: 0.75, borderRadius: 1,
          cursor: 'pointer', opacity: isHidden ? 0.3 : 1,
          transition: 'opacity 0.2s, background 0.15s',
          '&:hover': { bgcolor: 'action.hover' },
        }}>
        <Box sx={{
          width: 10, height: 10, flexShrink: 0, mt: '2px',
          borderRadius: '2px', bgcolor: activity.color,
        }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{
            fontSize: 12, fontWeight: isHidden ? 400 : 600,
            color: isHidden ? 'text.disabled' : activity.color, lineHeight: 1.3,
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'text.disabled', lineHeight: 1.3 }}>
            {activity.startDate} → {activity.endDate}
          </Typography>
        </Box>
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}
      >
        <MenuItem onClick={openDetails} sx={{ fontSize: 12 }}>Details</MenuItem>
      </Menu>

      <Beacon4ActivityDetailsDialog activity={activity} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </>
  )
}

export default function Beacon4PerformanceNetworkOverviewLegendPanel({ width = 220 }) {
  const dispatch        = useDispatch()
  const visibleLeft     = useSelector(selectBeacon4VisibleSeriesLeft)
  const visibleRight    = useSelector(selectBeacon4VisibleSeriesRight)
  const dashboardByTech = useSelector(selectBeacon4PerformanceNetworkOverviewDashboardByTech)
  const tech            = useSelector(selectBeacon4PerfTech)
  const mainKpiKey      = useSelector(selectBeacon4PerfMainKpi)
  const handleClick     = (panel, key) => dispatch(toggleBeacon4PanelSeries({ panel, key }))

  const activitiesVisible  = useSelector(selectBeacon4ActivitiesVisible)
  const selectedActivities = useSelector(selectBeacon4SelectedActivities)
  const visibleActivityIds = useSelector(selectBeacon4VisibleActivities)
  const handleActivityClick = (id) => dispatch(toggleBeacon4ActivityVisibility(id))
  // Same colour rotation the chart's markers use (see
  // Beacon4PerformanceNetworkOverviewView) — both derive from the same
  // selector + helper, so a swatch here always matches its marker pair.
  const activityItems = useMemo(() => assignActivityColors(selectedActivities), [selectedActivities])
  // Turning "Activities" off (Options) must make every trace of them
  // disappear here too, not just stop the FilterBar's own Activities group
  // from fetching — so the whole section (and its divider) is gated on the
  // toggle, not just on whether there happen to be any items.
  const showActivitiesSection = activitiesVisible

  const [hasData, setHasData] = useState(false)

  // Extract current dashboard data
  const data       = dashboardByTech?.[tech]?.[mainKpiKey]
  const leftPanel  = data?.leftPanel  ?? []
  const rightPanel = data?.rightPanel ?? []

  // Build legend items keyed by KPI name
  const leftItems = useMemo(() => (
    leftPanel.map((s, i) => ({
      key: s.kpiName, label: s.kpiName,
      color: getColorForKey(s.kpiName, i),
      _delay: i * 200,
    }))
  ), [leftPanel])

  const rightItems = useMemo(() => (
    rightPanel.map((s, i) => ({
      key: s.kpiName, label: s.kpiName,
      color: getColorForKey(s.kpiName, leftItems.length + i),
      _delay: (leftItems.length + i) * 200,
    }))
  ), [rightPanel, leftItems])

  useEffect(() => {
    if (leftPanel.length > 0 || rightPanel.length > 0) setHasData(true)
  }, [leftPanel, rightPanel])

  const showShimmer = hasData && visibleLeft === null && visibleRight === null
  const showHint    = hasData && visibleLeft === null && visibleRight === null

  // Kept mounted whenever there's KPI data OR the Activities section would
  // show something — a user who's only interested in Activities (e.g. right
  // after toggling it on, before ever pressing Apply) shouldn't lose the
  // panel entirely just because hasData hasn't flipped true yet.
  if (!hasData && !showActivitiesSection) return null

  return (
    <Drawer anchor="right" open variant="persistent" sx={{
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width, position: 'relative', height: '100%',
        border: 'none', borderLeft: '1px solid', borderColor: 'divider',
        bgcolor: '#fff', overflow: 'hidden',
      },
    }}>
      {/* Space is split evenly (flex: 1 each) between the KPI series legend
          (top) and the Activities list (bottom) — each scrolls
          independently, so a long list in one half never pushes the other
          one off-screen. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>

        {/* Hint — top, prominent */}
        {showHint && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 1, mb: 1, borderRadius: 1.5,
            bgcolor: 'primary.main',
            animation: 'b4p-hint 1.6s ease-in-out infinite',
            '@keyframes b4p-hint': {
              '0%':   { opacity: 0.8, transform: 'translateX(0px)'  },
              '50%':  { opacity: 1,   transform: 'translateX(4px)'  },
              '100%': { opacity: 0.8, transform: 'translateX(0px)'  },
            },
          }}>
            <Box component="svg" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth={2.5}
              sx={{ width: 16, height: 16, flexShrink: 0 }}>
              <path d="M5 12h14" />
              <path d="m13 5 7 7-7 7" />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
              Click a label to filter
            </Typography>
          </Box>
        )}

        {/* LEFT panel section */}
        {leftItems.length > 0 && (
          <>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 0.5, mt: 0.5 }}>
              KPI Performance
            </Typography>
            {leftItems.map(d => (
              <LegendItem
                key={d.key} d={d}
                isHidden={visibleLeft !== null && !visibleLeft.includes(d.key)}
                onClick={(k) => handleClick('left', k)}
                shimmer={showShimmer}
              />
            ))}
          </>
        )}

        {/* RIGHT panel section */}
        {rightItems.length > 0 && (
          <>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mt: 1.5, mb: 0.5 }}>
              KPI Performance (Aux)
            </Typography>
            {rightItems.map(d => (
              <LegendItem
                key={d.key} d={d}
                isHidden={visibleRight !== null && !visibleRight.includes(d.key)}
                onClick={(k) => handleClick('right', k)}
                shimmer={showShimmer}
              />
            ))}
          </>
        )}

        {/* Show all — resets series isolated via the legend above */}
        {(visibleLeft !== null || visibleRight !== null) && (
          <Box onClick={() => dispatch(resetBeacon4HiddenSeries())} sx={{
            fontSize: 11, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.75, mt: 1, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        )}
      </Box>

      {/* Divider + BOTTOM HALF — Activities. Both only render when the
          "Activities" toggle (Options) is on — turning it off must make
          every trace of activities disappear, including here, not just the
          FilterBar's own Activities group and the chart markers. When this
          section isn't rendered, the KPI half above simply fills the whole
          panel (still just flex: 1 with nothing to share the space with). */}
      {showActivitiesSection && (
        <>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }} />

          {/* Same colours as the chart's vertical markers
              (assignActivityColors, shared with Beacon4KpiLineChart via
              Beacon4PerformanceNetworkOverviewView). Lets the user match a
              marker pair on the chart to its activity without hovering it,
              and click a row to isolate it on the chart the same way KPI
              series work above. */}
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 0.5 }}>
              Activities
            </Typography>
            {activityItems.length > 0 ? (
              <>
                {activityItems.map(a => (
                  <ActivityLegendItem
                    key={a.id} activity={a}
                    isHidden={visibleActivityIds !== null && !visibleActivityIds.includes(String(a.id))}
                    onClick={handleActivityClick}
                  />
                ))}
                {visibleActivityIds !== null && (
                  <Box onClick={() => dispatch(resetBeacon4ActivityVisibility())} sx={{
                    fontSize: 11, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
                    textAlign: 'center', py: 0.75, mt: 1, borderRadius: 1,
                    border: '1px solid', borderColor: 'primary.light',
                    '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
                  }}>
                    ✕ Show all
                  </Box>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: 11, color: 'text.disabled', px: 1, fontStyle: 'italic' }}>
                No activities in range
              </Typography>
            )}
          </Box>
        </>
      )}

      </Box>
    </Drawer>
  )
}

// Export the palette helper so the view can match the legend colours
export { getColorForKey, PALETTE }